<template>
  <div class="tender-followups">
    <!-- Header -->
    <div class="row items-center justify-between q-mb-md">
      <div class="row items-center q-gutter-sm">
        <q-icon name="contact_phone" color="primary" size="24px" />
        <span class="text-h6">Followups</span>
        <q-badge v-if="followups.length > 0" color="primary" :label="followups.length" />
      </div>
      <q-btn
        color="primary"
        icon="add"
        label="Add Followup"
        @click="showAddDialog"
      />
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center q-pa-lg">
      <q-spinner-dots color="primary" size="40px" />
      <div class="q-mt-sm text-grey-7">Loading followups...</div>
    </div>

    <!-- Empty State -->
    <div v-else-if="followups.length === 0" class="empty-state">
      <q-icon name="phone_missed" size="64px" color="grey-4" />
      <div class="text-h6 text-grey-7 q-mt-md">No Followups Yet</div>
      <div class="text-body2 text-grey-6">
        Track your communications and follow-up activities with clients.
      </div>
      <q-btn
        color="primary"
        icon="add"
        label="Add First Followup"
        class="q-mt-md"
        @click="showAddDialog"
      />
    </div>

    <!-- Followups Timeline -->
    <q-timeline v-else color="primary" layout="loose" class="followups-timeline">
      <q-timeline-entry
        v-for="followup in sortedFollowups"
        :key="followup.id"
        :icon="getOutcomeIcon(followup.outcome)"
        :color="getOutcomeColor(followup.outcome)"
      >
        <template #title>
          <div class="row items-center q-gutter-sm">
            <span class="text-weight-bold">{{ formatDate(followup.followup_date) }}</span>
            <q-badge
              :color="getOutcomeColor(followup.outcome)"
              :label="getOutcomeLabel(followup.outcome)"
            />
          </div>
        </template>

        <template #subtitle>
          <div class="row items-center q-gutter-sm text-grey-7">
            <q-icon name="person" size="16px" />
            <span>{{ followup.user?.name || 'Unknown User' }}</span>
            <span v-if="followup.organization">
              <q-icon name="business" size="16px" class="q-ml-sm" />
              {{ followup.organization.name }}
            </span>
          </div>
        </template>

        <q-card flat bordered class="followup-card">
          <q-card-section>
            <!-- Comments -->
            <div v-if="followup.comments" class="followup-comments q-mb-md">
              {{ followup.comments }}
            </div>

            <!-- Next Followup -->
            <div v-if="followup.next_followup_date" class="row items-center q-gutter-sm text-orange-7">
              <q-icon name="event" size="16px" />
              <span class="text-caption">
                Next followup: {{ formatDate(followup.next_followup_date) }}
              </span>
            </div>

            <!-- Actions -->
            <div class="row justify-end q-mt-sm">
              <q-btn
                flat
                dense
                icon="edit"
                color="primary"
                @click="editFollowup(followup)"
              >
                <q-tooltip>Edit</q-tooltip>
              </q-btn>
              <q-btn
                flat
                dense
                icon="delete"
                color="negative"
                @click="confirmDeleteFollowup(followup)"
              >
                <q-tooltip>Delete</q-tooltip>
              </q-btn>
            </div>
          </q-card-section>
        </q-card>
      </q-timeline-entry>
    </q-timeline>

    <!-- Add/Edit Dialog -->
    <q-dialog v-model="showDialog" persistent>
      <q-card style="min-width: 500px; max-width: 90vw">
        <q-card-section class="row items-center">
          <q-avatar :color="editingFollowup ? 'orange' : 'primary'" text-color="white" icon="contact_phone" />
          <span class="q-ml-sm text-h6">
            {{ editingFollowup ? 'Edit Followup' : 'Add Followup' }}
          </span>
        </q-card-section>

        <q-card-section>
          <q-form ref="followupForm" class="q-gutter-md">
            <!-- Followup Date -->
            <q-input
              v-model="form.followup_date"
              type="date"
              label="Followup Date"
              outlined
              :rules="[val => !!val || 'Date is required']"
            >
              <template #prepend>
                <q-icon name="event" />
              </template>
            </q-input>

            <!-- Contractor (Optional) - Only shows contractors assigned to this tender -->
            <q-select
              v-model="form.organization_id"
              :options="contractorOptions"
              label="Contractor Contacted (Optional)"
              outlined
              emit-value
              map-options
              clearable
              option-value="organization_id"
              option-label="name"
              :hint="contractorOptions.length === 0 ? 'No contractors assigned to this tender yet' : ''"
            >
              <template #prepend>
                <q-icon name="business" />
              </template>
              <template #option="scope">
                <q-item v-bind="scope.itemProps">
                  <q-item-section avatar>
                    <q-avatar color="primary" text-color="white" size="32px">
                      {{ scope.opt.name?.charAt(0)?.toUpperCase() || '?' }}
                    </q-avatar>
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ scope.opt.name }}</q-item-label>
                    <q-item-label v-if="scope.opt.status" caption>
                      Status: {{ scope.opt.status }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </template>
              <template #selected-item="scope">
                <div class="row items-center">
                  <q-avatar color="primary" text-color="white" size="24px" class="q-mr-sm">
                    {{ scope.opt?.name?.charAt(0)?.toUpperCase() || '?' }}
                  </q-avatar>
                  {{ scope.opt?.name || 'Select contractor...' }}
                </div>
              </template>
            </q-select>

            <!-- Outcome -->
            <q-select
              v-model="form.outcome"
              :options="outcomeOptions"
              label="Outcome"
              outlined
              emit-value
              map-options
              clearable
            >
              <template #prepend>
                <q-icon name="assessment" />
              </template>
              <template #option="scope">
                <q-item v-bind="scope.itemProps">
                  <q-item-section avatar>
                    <q-icon :name="getOutcomeIcon(scope.opt.value)" :color="getOutcomeColor(scope.opt.value)" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ scope.opt.label }}</q-item-label>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>

            <!-- Comments -->
            <q-input
              v-model="form.comments"
              type="textarea"
              label="Comments / Notes"
              outlined
              autogrow
              rows="3"
              hint="What was discussed? Any important outcomes?"
            >
              <template #prepend>
                <q-icon name="notes" />
              </template>
            </q-input>

            <!-- Next Followup Date -->
            <q-input
              v-model="form.next_followup_date"
              type="date"
              label="Next Followup Date (Optional)"
              outlined
              hint="When should we follow up again?"
            >
              <template #prepend>
                <q-icon name="schedule" />
              </template>
            </q-input>
          </q-form>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn
            flat
            label="Cancel"
            @click="closeDialog"
          />
          <q-btn
            color="primary"
            :label="editingFollowup ? 'Update' : 'Save'"
            :loading="saving"
            @click="saveFollowup"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from 'vue';
  import { useQuasar } from 'quasar';
  import api from '../../services/api';

  const props = defineProps({
    tenderId: {
      type: [Number, String],
      required: true,
    },
    contractors: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits(['updated']);

  const $q = useQuasar();

  // State
  const loading = ref(false);
  const saving = ref(false);
  const followups = ref([]);
  const showDialog = ref(false);
  const editingFollowup = ref(null);
  const followupForm = ref(null);

  const form = ref({
    followup_date: '',
    organization_id: null,
    comments: '',
    outcome: null,
    next_followup_date: '',
  });

  // Options
  const outcomeOptions = [
    { label: 'Positive Response', value: 'positive' },
    { label: 'Negative Response', value: 'negative' },
    { label: 'No Response', value: 'no_response' },
    { label: 'Information Gathered', value: 'info_gathered' },
  ];

  // Computed
  const sortedFollowups = computed(() => {
    return [...followups.value].sort((a, b) => {
      return new Date(b.followup_date) - new Date(a.followup_date);
    });
  });

  // Build contractor options from tender's contractors
  // Each contractor in the tender has organization_id and organization.name
  const contractorOptions = computed(() => {
    if (!props.contractors || props.contractors.length === 0) {
      return [];
    }
    return props.contractors.map((contractor) => ({
      organization_id: contractor.organization_id || contractor.id,
      name: contractor.organization?.name || contractor.name || 'Unknown',
      status: contractor.status,
    }));
  });

  // Methods
  async function loadFollowups() {
    loading.value = true;
    try {
      followups.value = await api.get(`/tenders/${props.tenderId}/followups`);
    } catch (error) {
      console.error('Failed to load followups:', error);
      $q.notify({
        type: 'negative',
        message: 'Failed to load followups',
        timeout: 3000,
      });
    } finally {
      loading.value = false;
    }
  }

  function showAddDialog() {
    editingFollowup.value = null;
    form.value = {
      followup_date: new Date().toISOString().split('T')[0],
      organization_id: null,
      comments: '',
      outcome: null,
      next_followup_date: '',
    };
    showDialog.value = true;
  }

  function editFollowup(followup) {
    editingFollowup.value = followup;
    form.value = {
      followup_date: followup.followup_date,
      organization_id: followup.organization_id,
      comments: followup.comments || '',
      outcome: followup.outcome,
      next_followup_date: followup.next_followup_date || '',
    };
    showDialog.value = true;
  }

  function closeDialog() {
    showDialog.value = false;
    editingFollowup.value = null;
    form.value = {
      followup_date: '',
      organization_id: null,
      comments: '',
      outcome: null,
      next_followup_date: '',
    };
  }

  async function saveFollowup() {
    const valid = await followupForm.value.validate();
    if (!valid) {return;}

    saving.value = true;

    try {
      const data = {
        followup_date: form.value.followup_date,
        organization_id: form.value.organization_id || null,
        comments: form.value.comments || null,
        outcome: form.value.outcome || null,
        next_followup_date: form.value.next_followup_date || null,
      };

      if (editingFollowup.value) {
        await api.put(
          `/tenders/${props.tenderId}/followups/${editingFollowup.value.id}`,
          data,
        );
        $q.notify({
          type: 'positive',
          message: 'Followup updated successfully',
          timeout: 3000,
        });
      } else {
        await api.post(`/tenders/${props.tenderId}/followups`, data);
        $q.notify({
          type: 'positive',
          message: 'Followup added successfully',
          timeout: 3000,
        });
      }

      closeDialog();
      await loadFollowups();
      emit('updated');
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to save followup: ${error.message}`,
        timeout: 5000,
      });
    } finally {
      saving.value = false;
    }
  }

  function confirmDeleteFollowup(followup) {
    $q.dialog({
      title: 'Delete Followup',
      message: 'Are you sure you want to delete this followup?',
      cancel: true,
      persistent: true,
    }).onOk(async () => {
      try {
        await api.delete(`/tenders/${props.tenderId}/followups/${followup.id}`);
        $q.notify({
          type: 'positive',
          message: 'Followup deleted',
          timeout: 3000,
        });
        await loadFollowups();
        emit('updated');
      } catch (error) {
        $q.notify({
          type: 'negative',
          message: `Failed to delete followup: ${error.message}`,
          timeout: 5000,
        });
      }
    });
  }

  function formatDate(dateString) {
    if (!dateString) {return '';}
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  function getOutcomeIcon(outcome) {
    const icons = {
      positive: 'thumb_up',
      negative: 'thumb_down',
      no_response: 'hourglass_empty',
      info_gathered: 'info',
    };
    return icons[outcome] || 'phone';
  }

  function getOutcomeColor(outcome) {
    const colors = {
      positive: 'positive',
      negative: 'negative',
      no_response: 'grey',
      info_gathered: 'info',
    };
    return colors[outcome] || 'primary';
  }

  function getOutcomeLabel(outcome) {
    const option = outcomeOptions.find((o) => o.value === outcome);
    return option?.label || outcome || 'No Outcome';
  }

  // Lifecycle
  onMounted(() => {
    loadFollowups();
  });
</script>

<style lang="scss" scoped>
.tender-followups {
  .empty-state {
    text-align: center;
    padding: 48px 24px;
  }

  .followups-timeline {
    :deep(.q-timeline__entry) {
      padding-bottom: 24px;
    }
  }

  .followup-card {
    margin-top: 8px;
    background: #fafafa;

    .followup-comments {
      font-size: 0.95rem;
      line-height: 1.6;
      color: #333;
      white-space: pre-wrap;
    }
  }
}
</style>
